import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { Dialog, DialogActions } from "@/components/primitives/Dialog";
import { Button } from "@/components/primitives/Button";

export type ConfirmOptions = {
	title: string;
	description?: string;
	confirmLabel?: string;
	cancelLabel?: string;
	variant?: "danger" | "primary";
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

type ConfirmContextValue = {
	confirm: ConfirmFn;
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

type Pending = ConfirmOptions & {
	resolve: (value: boolean) => void;
};

export function ConfirmProvider({ children }: { children: ReactNode }) {
	const [pending, setPending] = useState<Pending | null>(null);
	const pendingRef = useRef<Pending | null>(null);

	const close = useCallback((value: boolean) => {
		const current = pendingRef.current;
		pendingRef.current = null;
		setPending(null);
		current?.resolve(value);
	}, []);

	const confirm = useCallback<ConfirmFn>((options) => {
		return new Promise<boolean>((resolve) => {
			const prev = pendingRef.current;
			if (prev) prev.resolve(false);
			const next: Pending = { ...options, resolve };
			pendingRef.current = next;
			setPending(next);
		});
	}, []);

	const value = useMemo(() => ({ confirm }), [confirm]);

	return (
		<ConfirmContext.Provider value={value}>
			{children}
			<Dialog
				open={pending !== null}
				onClose={() => close(false)}
				title={pending?.title ?? ""}
				description={pending?.description}
				showClose={false}
				className="max-w-sm"
			>
				<DialogActions>
					<Button variant="secondary" size="sm" onClick={() => close(false)}>
						{pending?.cancelLabel ?? "Abbrechen"}
					</Button>
					<Button
						variant={pending?.variant === "primary" ? "primary" : "danger"}
						size="sm"
						onClick={() => close(true)}
						autoFocus
					>
						{pending?.confirmLabel ?? "Bestätigen"}
					</Button>
				</DialogActions>
			</Dialog>
		</ConfirmContext.Provider>
	);
}

export function useConfirm(): ConfirmFn {
	const ctx = useContext(ConfirmContext);
	if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
	return ctx.confirm;
}
